import { z } from 'zod';

export const PROOF_EXTRACTION_CONTRACT_SCHEMA_VERSION = 'proof-extraction-contract/v1' as const;
export const PROOF_EXTRACTION_MANIFEST_SCHEMA_VERSION = 'proof-extraction-manifest/v1' as const;
export const PROOF_EXTRACTION_BUDGET_POLICY_VERSION = 'proof-extraction-budget/v1' as const;

export const proofIdentifierSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/);
export const proofSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const proofDecimalSchema = z
  .string()
  .regex(/^-?(?:0|[1-9]\d*)(?:\.\d*[1-9])?$/)
  .refine((value) => value !== '-0', 'Negative zero is not canonical.');
export const proofDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Date must be a real calendar date.');

export const PROOF_EXTRACTION_ADAPTERS = [
  'pdf',
  'image',
  'csv',
  'xlsx',
  'docx',
  'pptx',
  'text',
  'markdown',
  'html',
] as const;

export const PROOF_EXTRACTION_OBJECT_CLASSES = [
  'document',
  'page',
  'sheet',
  'slide',
  'section',
  'text_block',
  'table',
  'chart',
  'image',
  'annotation',
  'cell_range',
  'formula',
  'comment',
  'hidden_marker',
  'header',
  'footer',
  'footnote',
  'endnote',
  'note',
  'tracked_change',
  'drawing',
  'dom_section',
] as const;

export const PROOF_EXTRACTION_VALUE_AREAS = [
  'discovery',
  'brand_ad_revenue',
  'throughput_labor',
  'menu_compliance',
  'loyalty_retention',
  'fulfillment',
  'experience',
  'shopper_insight',
] as const;

export const PROOF_EXTRACTION_EVIDENCE_MODES = ['direct', 'derived', 'estimated', 'qualitative'] as const;
export const PROOF_EVIDENCE_RELATIONS = [
  'same_evidence',
  'same_claim',
  'corroborates',
  'contradicts',
  'supersedes',
] as const;

export const proofExtractionAdapterSchema = z.enum(PROOF_EXTRACTION_ADAPTERS);
export const proofExtractionObjectClassSchema = z.enum(PROOF_EXTRACTION_OBJECT_CLASSES);
export const proofValueAreaSchema = z.enum(PROOF_EXTRACTION_VALUE_AREAS);
export const proofEvidenceModeSchema = z.enum(PROOF_EXTRACTION_EVIDENCE_MODES);
export const proofEvidenceRelationSchema = z.enum(PROOF_EVIDENCE_RELATIONS);
export const proofCandidateImportanceSchema = z.enum(['relevant', 'valuable', 'critical']);

export const proofCitationLocatorSchema = z.union([
  z
    .object({
      kind: z.literal('text_span'),
      unit: z.string().min(1).max(240),
      start: z.number().int().min(0),
      end: z.number().int().positive(),
      textHash: proofSha256Schema,
    })
    .strict()
    .refine((value) => value.end > value.start, 'Text span end must be greater than start.'),
  z
    .object({
      kind: z.literal('cell_range'),
      sheet: z.string().min(1).max(160),
      range: z.string().regex(/^[A-Z]+\d+(?::[A-Z]+\d+)?$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal('formula'),
      sheet: z.string().min(1).max(160),
      cell: z.string().regex(/^[A-Z]+\d+$/),
      formulaHash: proofSha256Schema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('bounding_box'),
      unit: z.string().min(1).max(240),
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
      width: z.number().positive().max(1),
      height: z.number().positive().max(1),
    })
    .strict()
    .refine((value) => value.x + value.width <= 1 && value.y + value.height <= 1, {
      message: 'Bounding box must remain within normalized source bounds.',
    }),
  z
    .object({
      kind: z.literal('object_path'),
      path: z.string().min(1).max(1_000),
    })
    .strict(),
]);

export type ProofCitationLocator = z.infer<typeof proofCitationLocatorSchema>;

export function serializeProofCitationLocator(locator: ProofCitationLocator): string {
  const parsed = proofCitationLocatorSchema.parse(locator);
  switch (parsed.kind) {
    case 'text_span':
      return JSON.stringify({
        kind: parsed.kind,
        unit: parsed.unit,
        start: parsed.start,
        end: parsed.end,
        textHash: parsed.textHash,
      });
    case 'cell_range':
      return JSON.stringify({ kind: parsed.kind, sheet: parsed.sheet, range: parsed.range });
    case 'formula':
      return JSON.stringify({
        kind: parsed.kind,
        sheet: parsed.sheet,
        cell: parsed.cell,
        formulaHash: parsed.formulaHash,
      });
    case 'bounding_box':
      return JSON.stringify({
        kind: parsed.kind,
        unit: parsed.unit,
        x: parsed.x,
        y: parsed.y,
        width: parsed.width,
        height: parsed.height,
      });
    case 'object_path':
      return JSON.stringify({ kind: parsed.kind, path: parsed.path });
  }
}

export function parseProofCitationLocator(serialized: string): ProofCitationLocator {
  return proofCitationLocatorSchema.parse(JSON.parse(serialized) as unknown);
}

export const PROOF_RUN_STATES = [
  'draft_uploading',
  'queued',
  'processing',
  'verifying',
  'complete',
  'cancel_requested',
  'cancelled',
  'partial',
  'failed',
  'incomplete_budget_exceeded',
] as const;
export const PROOF_ASSET_STATES = [
  'staged',
  'validating',
  'ready',
  'processing',
  'processed',
  'rejected',
  'partial',
  'failed',
  'unsupported',
  'excluded',
] as const;
export const PROOF_OBJECT_STATES = ['pending', 'processing', 'processed', 'partial', 'failed', 'unsupported', 'excluded'] as const;
export const PROOF_CANDIDATE_STATES = ['proposed', 'model_verified', 'selected', 'created', 'rejected', 'duplicate'] as const;
export const PROOF_CANDIDATE_APPROVAL_STATES = ['active', 'consumed', 'revoked', 'stale'] as const;
export const PROOF_GOVERNANCE_STATES = [
  'draft_unverified',
  'draft_human_verified',
  'pending_approval',
  'approved',
  'expired',
  'revoked',
] as const;

export const proofRunStateSchema = z.enum(PROOF_RUN_STATES);
export const proofAssetStateSchema = z.enum(PROOF_ASSET_STATES);
export const proofObjectStateSchema = z.enum(PROOF_OBJECT_STATES);
export const proofCandidateStateSchema = z.enum(PROOF_CANDIDATE_STATES);
export const proofCandidateApprovalStateSchema = z.enum(PROOF_CANDIDATE_APPROVAL_STATES);
export const proofGovernanceStateSchema = z.enum(PROOF_GOVERNANCE_STATES);

export const proofAssetManifestSchema = z
  .object({
    assetId: proofIdentifierSchema,
    assetHash: proofSha256Schema,
    adapter: proofExtractionAdapterSchema,
    adapterVersion: z.string().min(1).max(120),
    generation: z.number().int().positive(),
    state: proofAssetStateSchema,
    bytes: z.number().int().min(0),
    expectedTopLevelUnits: z.number().int().min(0),
    detectedTopLevelUnits: z.number().int().min(0),
    expectedObjectCount: z.number().int().min(0),
    detectedObjectCount: z.number().int().min(0),
    excludedReason: z.string().min(1).max(500).nullable(),
    errorCode: z.string().min(1).max(120).nullable(),
  })
  .strict()
  .superRefine((asset, context) => {
    if (asset.bytes > PROOF_EXTRACTION_LIMITS.bytesPerAsset) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['bytes'], message: 'Asset exceeds the v1 byte limit.' });
    }
    if (asset.expectedObjectCount > PROOF_EXTRACTION_LIMITS.childObjectsPerAsset) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['expectedObjectCount'], message: 'Asset exceeds the v1 object limit.' });
    }
    if (asset.state === 'excluded' && !asset.excludedReason) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['excludedReason'], message: 'Excluded assets require a reason.' });
    }
    if (asset.state === 'processed') {
      if (asset.expectedTopLevelUnits !== asset.detectedTopLevelUnits) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['detectedTopLevelUnits'], message: 'Processed asset unit count must reconcile.' });
      }
      if (asset.expectedObjectCount !== asset.detectedObjectCount) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['detectedObjectCount'], message: 'Processed asset object count must reconcile.' });
      }
    }
    if (asset.state !== 'excluded' && asset.excludedReason) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['excludedReason'], message: 'Only excluded assets may contain an exclusion reason.' });
    }
  });

export const proofExtractionManifestSchema = z
  .object({
    schemaVersion: z.literal(PROOF_EXTRACTION_MANIFEST_SCHEMA_VERSION),
    runId: proofIdentifierSchema,
    runGeneration: z.number().int().positive(),
    runState: proofRunStateSchema,
    bundleFingerprint: proofSha256Schema,
    expectedAssetCount: z.number().int().min(1),
    expectedTopLevelUnits: z.number().int().min(0),
    assets: z.array(proofAssetManifestSchema).min(1).max(10),
  })
  .strict()
  .superRefine((manifest, context) => {
    if (manifest.assets.length !== manifest.expectedAssetCount) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['assets'], message: 'Asset count does not match manifest.' });
    }
    const units = manifest.assets.reduce((sum, asset) => sum + asset.expectedTopLevelUnits, 0);
    if (units !== manifest.expectedTopLevelUnits) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['expectedTopLevelUnits'], message: 'Top-level units do not reconcile.' });
    }
    if (manifest.expectedTopLevelUnits > PROOF_EXTRACTION_LIMITS.topLevelUnits) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['expectedTopLevelUnits'], message: 'Bundle exceeds the v1 top-level unit limit.' });
    }
    const bytes = manifest.assets.reduce((sum, asset) => sum + asset.bytes, 0);
    if (bytes > PROOF_EXTRACTION_LIMITS.bytesPerBundle) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['assets'], message: 'Bundle exceeds the v1 byte limit.' });
    }
    const objects = manifest.assets.reduce((sum, asset) => sum + asset.expectedObjectCount, 0);
    if (objects > PROOF_EXTRACTION_LIMITS.childObjectsPerBundle) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['assets'], message: 'Bundle exceeds the v1 object limit.' });
    }
  });

export const proofSourceObjectSchema = z
  .object({
    objectId: proofIdentifierSchema,
    assetId: proofIdentifierSchema,
    parentObjectId: proofIdentifierSchema.nullable(),
    objectClass: proofExtractionObjectClassSchema,
    locator: proofCitationLocatorSchema,
    assetHash: proofSha256Schema,
    adapterVersion: z.string().min(1).max(120),
    generation: z.number().int().positive(),
    state: proofObjectStateSchema,
    expectedChildCount: z.number().int().min(0),
    detectedChildCount: z.number().int().min(0),
    contentHash: proofSha256Schema.nullable(),
    excludedReason: z.string().min(1).max(500).nullable(),
    errorCode: z.string().min(1).max(120).nullable(),
  })
  .strict()
  .superRefine((object, context) => {
    if (object.state === 'excluded' && !object.excludedReason) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['excludedReason'], message: 'Excluded objects require a reason.' });
    }
    if (object.state === 'processed' && object.expectedChildCount !== object.detectedChildCount) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['detectedChildCount'], message: 'Processed object child count must reconcile.' });
    }
    if (object.state === 'processed' && !object.contentHash) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['contentHash'], message: 'Processed objects require an immutable content hash.' });
    }
    if (object.state !== 'excluded' && object.excludedReason) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['excludedReason'], message: 'Only excluded objects may contain an exclusion reason.' });
    }
  });

export const proofCitationSchema = z
  .object({
    citationId: proofIdentifierSchema,
    assetId: proofIdentifierSchema,
    objectId: proofIdentifierSchema,
    locator: proofCitationLocatorSchema,
    evidenceHash: proofSha256Schema,
    support: z.enum(['entails', 'operand', 'caveat', 'counterevidence']),
  })
  .strict();

export const proofEvidenceSetSchema = z
  .object({
    evidenceSetId: proofIdentifierSchema,
    evidenceSetHash: proofSha256Schema,
    assets: z
      .array(
        z
          .object({
            assetId: proofIdentifierSchema,
            assetHash: proofSha256Schema,
          })
          .strict(),
      )
      .min(1),
    citations: z.array(proofCitationSchema).min(1),
  })
  .strict()
  .superRefine((evidenceSet, context) => {
    if (new Set(evidenceSet.assets.map((asset) => asset.assetId)).size !== evidenceSet.assets.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['assets'], message: 'Evidence-set asset IDs must be unique.' });
    }
    if (new Set(evidenceSet.citations.map((citation) => citation.citationId)).size !== evidenceSet.citations.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['citations'], message: 'Evidence-set citation IDs must be unique.' });
    }
    const assetIds = new Set(evidenceSet.assets.map((asset) => asset.assetId));
    for (const citation of evidenceSet.citations) {
      if (!assetIds.has(citation.assetId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['citations'], message: `Citation ${citation.citationId} references an unknown asset.` });
      }
    }
  });

export const proofNumericOperandSchema = z
  .object({
    name: z.string().min(1).max(80),
    value: proofDecimalSchema,
    unit: z.string().min(1).max(80),
    citationId: proofIdentifierSchema,
  })
  .strict();

export const proofNumericEvidenceSchema = z
  .object({
    value: proofDecimalSchema,
    unit: z.string().min(1).max(80),
    direction: z.enum(['increase', 'decrease', 'neutral', 'not_applicable']),
    numerator: proofDecimalSchema.nullable(),
    denominator: proofDecimalSchema.nullable(),
    baseline: proofDecimalSchema.nullable(),
    comparison: proofDecimalSchema.nullable(),
    formula: z.string().min(1).max(500).nullable(),
    operands: z.array(proofNumericOperandSchema),
    rounding: z.string().min(1).max(120),
  })
  .strict();

export const proofMetricTypeSchema = z.enum(['percent_lift', 'absolute', 'rate', 'count', 'qualitative']);
export const proofTypeSchema = z.enum(['performance_metric', 'benchmark', 'case_study', 'survey', 'customer_review', 'qualitative_claim']);
export const proofStrengthSchema = z.enum(['hard_metric', 'case_study', 'survey', 'social_proof', 'qualitative']);
export const proofSourceTypeSchema = z.enum([
  'performance_brief',
  'pulse_report',
  'internal_study',
  'customer_provided',
  'benchmark_aggregate',
  'public_web',
]);

export const proofModelVerificationSchema = z
  .object({
    fingerprint: proofSha256Schema,
    verifiedAt: z.string().datetime(),
    verifierVersion: z.string().min(1).max(120),
  })
  .strict();

export const proofHumanVerificationSchema = z
  .object({
    proofId: proofIdentifierSchema,
    claimFingerprint: proofSha256Schema,
    claimFingerprintSchemaVersion: z.literal('proof-claim-fingerprint/v1'),
    evidenceSetId: proofIdentifierSchema,
    verifiedBy: proofIdentifierSchema,
    verifiedAt: z.string().datetime(),
  })
  .strict();

export const proofPipelineVersionSchema = z
  .object({
    model: z.string().min(1).max(160),
    promptVersion: z.string().min(1).max(120),
    candidateSchemaVersion: z.string().min(1).max(120),
    verifierVersion: z.string().min(1).max(120),
    adapterVersions: z.record(proofExtractionAdapterSchema, z.string().min(1).max(120)),
  })
  .strict();

export const proofCandidateSchema = z
  .object({
    schemaVersion: z.literal(PROOF_EXTRACTION_CONTRACT_SCHEMA_VERSION),
    candidateId: proofIdentifierSchema,
    runId: proofIdentifierSchema,
    runGeneration: z.number().int().positive(),
    revision: z.number().int().positive(),
    fingerprint: proofSha256Schema,
    pipeline: proofPipelineVersionSchema,
    state: proofCandidateStateSchema,
    importance: proofCandidateImportanceSchema,
    criticalNumeric: z.boolean(),
    labelShort: z.string().min(1).max(160),
    claimText: z.string().min(1).max(2_000),
    valueDisplay: z.string().min(1).max(240),
    metricType: proofMetricTypeSchema,
    proofType: proofTypeSchema.nullable(),
    proofStrength: proofStrengthSchema.nullable(),
    timeWindow: z.string().max(240),
    sourceType: proofSourceTypeSchema,
    sourcePeriodStart: proofDateSchema.nullable(),
    sourcePeriodEnd: proofDateSchema.nullable(),
    sourcePeriodDateKind: z.enum(['observed', 'mixed', 'unknown']),
    locationIds: z.array(proofIdentifierSchema),
    valueAreas: z.array(proofValueAreaSchema),
    evidenceMode: proofEvidenceModeSchema,
    citations: z.array(proofCitationSchema).min(1),
    numericEvidence: proofNumericEvidenceSchema.nullable(),
    limitations: z.array(z.string().min(1).max(500)),
    unresolvedContradictionCount: z.number().int().min(0),
    modelVerification: proofModelVerificationSchema.nullable(),
    createdProofId: proofIdentifierSchema.nullable(),
  })
  .strict()
  .superRefine((candidate, context) => {
    if (candidate.criticalNumeric && !candidate.numericEvidence) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['numericEvidence'], message: 'Critical numeric proof requires numeric evidence.' });
    }
    const citationIds = new Set(candidate.citations.map((citation) => citation.citationId));
    if (citationIds.size !== candidate.citations.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['citations'], message: 'Citation IDs must be unique.' });
    }
    const citationsById = new Map(candidate.citations.map((citation) => [citation.citationId, citation]));
    for (const operand of candidate.numericEvidence?.operands ?? []) {
      const citation = citationsById.get(operand.citationId);
      if (!citation) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['numericEvidence'], message: `Unknown operand citation ${operand.citationId}.` });
      } else if (citation.support !== 'operand') {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['numericEvidence'], message: `Operand citation ${operand.citationId} must have operand support.` });
      }
    }
    if (candidate.sourcePeriodDateKind === 'observed' && (!candidate.sourcePeriodStart || !candidate.sourcePeriodEnd)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourcePeriodDateKind'], message: 'Observed period requires both dates.' });
    }
    if (candidate.sourcePeriodDateKind === 'unknown' && (candidate.sourcePeriodStart || candidate.sourcePeriodEnd)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourcePeriodDateKind'], message: 'Unknown period cannot contain observation dates.' });
    }
    if (candidate.sourcePeriodDateKind === 'mixed' && !candidate.sourcePeriodStart && !candidate.sourcePeriodEnd) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourcePeriodDateKind'], message: 'Mixed period requires at least one observation date.' });
    }
    if (candidate.sourcePeriodStart && candidate.sourcePeriodEnd && candidate.sourcePeriodStart > candidate.sourcePeriodEnd) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourcePeriodEnd'], message: 'Source period end cannot precede start.' });
    }
    if (candidate.state === 'proposed' && candidate.modelVerification) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['modelVerification'], message: 'Proposed candidates cannot retain model verification.' });
    }
    if (['model_verified', 'selected', 'created'].includes(candidate.state)) {
      if (!candidate.modelVerification || candidate.modelVerification.fingerprint !== candidate.fingerprint) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['modelVerification'], message: 'Current model verification is required.' });
      }
    }
    if (
      candidate.modelVerification &&
      candidate.modelVerification.verifierVersion !== candidate.pipeline.verifierVersion
    ) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['modelVerification'], message: 'Verifier version does not match the candidate pipeline.' });
    }
    if (candidate.state === 'created' && !candidate.createdProofId) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['createdProofId'], message: 'Created candidates require a proof ID.' });
    }
    if (candidate.state !== 'created' && candidate.createdProofId) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['createdProofId'], message: 'Only created candidates may reference a proof ID.' });
    }
  });

export const proofEvidenceRelationRecordSchema = z
  .object({
    relationId: proofIdentifierSchema,
    fromCandidateId: proofIdentifierSchema,
    toCandidateId: proofIdentifierSchema,
    relation: proofEvidenceRelationSchema,
    evidenceHash: proofSha256Schema,
    rationale: z.string().min(1).max(500),
    resolvedAt: z.string().datetime().nullable(),
  })
  .strict()
  .refine((relation) => relation.fromCandidateId !== relation.toCandidateId, 'A candidate cannot relate to itself.');

export const proofEvidenceGraphSchema = z
  .object({
    schemaVersion: z.literal(PROOF_EXTRACTION_CONTRACT_SCHEMA_VERSION),
    runId: proofIdentifierSchema,
    runGeneration: z.number().int().positive(),
    objectIds: z.array(proofIdentifierSchema),
    candidateIds: z.array(proofIdentifierSchema),
    relations: z.array(proofEvidenceRelationRecordSchema),
  })
  .strict()
  .superRefine((graph, context) => {
    const candidates = new Set(graph.candidateIds);
    for (const relation of graph.relations) {
      if (!candidates.has(relation.fromCandidateId) || !candidates.has(relation.toCandidateId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['relations'], message: 'Relation references an unknown candidate.' });
      }
    }
  });

export const proofCoverageAuditSchema = z
  .object({
    schemaVersion: z.literal(PROOF_EXTRACTION_CONTRACT_SCHEMA_VERSION),
    runId: proofIdentifierSchema,
    runGeneration: z.number().int().positive(),
    expectedAssets: z.number().int().min(1),
    accountedAssets: z.number().int().min(0),
    expectedTopLevelUnits: z.number().int().min(0),
    accountedTopLevelUnits: z.number().int().min(0),
    expectedObjects: z.number().int().min(0),
    accountedObjects: z.number().int().min(0),
    unresolvedContradictions: z.number().int().min(0),
    activeLeases: z.number().int().min(0),
    usageReconciled: z.boolean(),
    passed: z.boolean(),
  })
  .strict()
  .superRefine((audit, context) => {
    const actuallyPassed =
      audit.expectedAssets === audit.accountedAssets &&
      audit.expectedTopLevelUnits === audit.accountedTopLevelUnits &&
      audit.expectedObjects === audit.accountedObjects &&
      audit.unresolvedContradictions === 0 &&
      audit.activeLeases === 0 &&
      audit.usageReconciled;
    if (audit.passed !== actuallyPassed) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['passed'], message: 'Coverage pass flag does not match accounted state.' });
    }
  });

export const PROOF_EXTRACTION_LIMITS = {
  assetsPerBundle: 10,
  bytesPerAsset: 25 * 1024 * 1024,
  bytesPerBundle: 100 * 1024 * 1024,
  topLevelUnits: 250,
  childObjectsPerAsset: 1_000,
  childObjectsPerBundle: 5_000,
  cellsPerWorkbook: 250_000,
  formulasPerWorkbook: 50_000,
  sheetsPerWorkbook: 100,
  officeXmlNodes: 250_000,
  officeArchiveEntries: 1_000,
  decompressedOfficeBytes: 250 * 1024 * 1024,
  officeCompressionRatio: 100,
  pixelsPerImage: 40_000_000,
  pixelsPerBundle: 250_000_000,
  extractedCharactersPerAsset: 2_000_000,
  extractedCharactersPerBundle: 10_000_000,
  aiTasksPerRun: 500,
  inputTokensPerRun: 2_000_000,
  outputTokensPerRun: 100_000,
  concurrentRunsPerOperator: 2,
  runCostMicrodollars: 5_000_000,
  operatorDayCostMicrodollars: 25_000_000,
  projectDayCostMicrodollars: 100_000_000,
} as const;

export const proofBudgetLedgerSchema = z
  .object({
    schemaVersion: z.literal(PROOF_EXTRACTION_BUDGET_POLICY_VERSION),
    runId: proofIdentifierSchema,
    operatorId: proofIdentifierSchema,
    utcDay: proofDateSchema,
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    requestedReservationMicrodollars: z.number().int().min(0),
    runCommittedMicrodollars: z.number().int().min(0),
    operatorDayCommittedMicrodollars: z.number().int().min(0),
    projectDayCommittedMicrodollars: z.number().int().min(0),
    actualMicrodollars: z.number().int().min(0),
    runCapMicrodollars: z.number().int().positive(),
    operatorDayCapMicrodollars: z.number().int().positive(),
    projectDayCapMicrodollars: z.number().int().positive(),
    usageReconciled: z.boolean(),
  })
  .strict()
  .superRefine((ledger, context) => {
    if (ledger.inputTokens > PROOF_EXTRACTION_LIMITS.inputTokensPerRun) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['inputTokens'], message: 'Input token usage exceeds the v1 run limit.' });
    }
    if (ledger.outputTokens > PROOF_EXTRACTION_LIMITS.outputTokensPerRun) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['outputTokens'], message: 'Output token usage exceeds the v1 run limit.' });
    }
    const configuredCaps: Array<[string, number, number]> = [
      ['runCapMicrodollars', ledger.runCapMicrodollars, PROOF_EXTRACTION_LIMITS.runCostMicrodollars],
      ['operatorDayCapMicrodollars', ledger.operatorDayCapMicrodollars, PROOF_EXTRACTION_LIMITS.operatorDayCostMicrodollars],
      ['projectDayCapMicrodollars', ledger.projectDayCapMicrodollars, PROOF_EXTRACTION_LIMITS.projectDayCostMicrodollars],
    ];
    for (const [field, actual, expected] of configuredCaps) {
      if (actual !== expected) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Ledger cap does not match the pinned v1 policy.' });
      }
    }
    if (ledger.actualMicrodollars > ledger.requestedReservationMicrodollars) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['actualMicrodollars'], message: 'Actual cost cannot exceed reservation.' });
    }
    const capChecks: Array<[string, number, number]> = [
      ['runCommittedMicrodollars', ledger.runCommittedMicrodollars, ledger.runCapMicrodollars],
      ['operatorDayCommittedMicrodollars', ledger.operatorDayCommittedMicrodollars, ledger.operatorDayCapMicrodollars],
      ['projectDayCommittedMicrodollars', ledger.projectDayCommittedMicrodollars, ledger.projectDayCapMicrodollars],
    ];
    for (const [field, committed, cap] of capChecks) {
      if (committed + ledger.requestedReservationMicrodollars > cap) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Reservation would exceed its spend cap.' });
      }
    }
  });

export const PROOF_EXTRACTION_ERROR_CODES = [
  'authentication_required',
  'operator_required',
  'proof_admin_required',
  'cross_user_access_denied',
  'capability_disabled',
  'data_controls_unverified',
  'invalid_request',
  'invalid_state_transition',
  'stale_revision',
  'stale_worker_write',
  'idempotency_conflict',
  'source_limit_exceeded',
  'source_type_unsupported',
  'source_validation_failed',
  'source_fetch_blocked',
  'coverage_incomplete',
  'contradiction_unresolved',
  'verification_required',
  'candidate_approval_required',
  'approval_fingerprint_mismatch',
  'budget_exceeded',
  'usage_reconciliation_required',
  'lease_unavailable',
  'cleanup_failed',
  'provider_unavailable',
  'internal_error',
] as const;

export const proofExtractionErrorCodeSchema = z.enum(PROOF_EXTRACTION_ERROR_CODES);
export type ProofExtractionErrorCode = z.infer<typeof proofExtractionErrorCodeSchema>;

export const PROOF_EXTRACTION_ERROR_POLICY: Record<ProofExtractionErrorCode, { httpStatus: number; retryable: boolean }> = {
  authentication_required: { httpStatus: 401, retryable: false },
  operator_required: { httpStatus: 403, retryable: false },
  proof_admin_required: { httpStatus: 403, retryable: false },
  cross_user_access_denied: { httpStatus: 403, retryable: false },
  capability_disabled: { httpStatus: 503, retryable: true },
  data_controls_unverified: { httpStatus: 503, retryable: false },
  invalid_request: { httpStatus: 400, retryable: false },
  invalid_state_transition: { httpStatus: 409, retryable: false },
  stale_revision: { httpStatus: 409, retryable: false },
  stale_worker_write: { httpStatus: 409, retryable: false },
  idempotency_conflict: { httpStatus: 409, retryable: false },
  source_limit_exceeded: { httpStatus: 413, retryable: false },
  source_type_unsupported: { httpStatus: 415, retryable: false },
  source_validation_failed: { httpStatus: 422, retryable: false },
  source_fetch_blocked: { httpStatus: 422, retryable: false },
  coverage_incomplete: { httpStatus: 409, retryable: true },
  contradiction_unresolved: { httpStatus: 409, retryable: false },
  verification_required: { httpStatus: 409, retryable: false },
  candidate_approval_required: { httpStatus: 409, retryable: false },
  approval_fingerprint_mismatch: { httpStatus: 409, retryable: false },
  budget_exceeded: { httpStatus: 429, retryable: true },
  usage_reconciliation_required: { httpStatus: 503, retryable: true },
  lease_unavailable: { httpStatus: 409, retryable: true },
  cleanup_failed: { httpStatus: 503, retryable: true },
  provider_unavailable: { httpStatus: 503, retryable: true },
  internal_error: { httpStatus: 500, retryable: true },
};

export const proofExtractionErrorResponseSchema = z
  .object({
    error: z
      .object({
        code: proofExtractionErrorCodeSchema,
        message: z.string().min(1).max(500),
        retryable: z.boolean(),
        request_id: proofIdentifierSchema,
        details: z
          .record(
            z
              .string()
              .regex(/^(?:[a-z][a-z0-9_]*(?:_id|_ids|_count|_state|_generation|_revision|_limit|_seconds)|count|state|generation|revision|limit)$/),
            z.union([proofIdentifierSchema, z.number().finite(), z.boolean(), z.array(proofIdentifierSchema)]),
          )
          .optional(),
      })
      .strict(),
  })
  .strict()
  .superRefine((response, context) => {
    if (response.error.retryable !== PROOF_EXTRACTION_ERROR_POLICY[response.error.code].retryable) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['error', 'retryable'], message: 'Retryable flag does not match error policy.' });
    }
  });

export type ProofRunState = z.infer<typeof proofRunStateSchema>;
export type ProofAssetState = z.infer<typeof proofAssetStateSchema>;
export type ProofObjectState = z.infer<typeof proofObjectStateSchema>;
export type ProofCandidateState = z.infer<typeof proofCandidateStateSchema>;
export type ProofCandidateApprovalState = z.infer<typeof proofCandidateApprovalStateSchema>;
export type ProofGovernanceState = z.infer<typeof proofGovernanceStateSchema>;
export type ProofAssetManifest = z.infer<typeof proofAssetManifestSchema>;
export type ProofSourceObject = z.infer<typeof proofSourceObjectSchema>;
export type ProofCandidate = z.infer<typeof proofCandidateSchema>;
export type ProofCoverageAudit = z.infer<typeof proofCoverageAuditSchema>;
